import { defineStore } from 'pinia';
import { toRaw } from 'vue';
import type { SampleTableRowDto } from '@/ApiClient';
import { CACHE_TTL, onTabResume } from '@/store/cache-ttl';
import { keyedLruCache } from '@/store/cache-primitives';

export interface SampleTableRowCache {
  filterKey: string;
  rows: SampleTableRowDto[];
  totalRowCount: number;
  roundOffset: number;
  hasMore: boolean;
}

// Row caches are keyed per filter combination (grouping/sort included — they change the
// query) so switching back and forth restores instantly. Rows are heavy (50 per page),
// so the cap is tighter than the id-based list caches.
const MAX_ROW_CACHES = 10;

// The table store decorates rows with _-prefixed span metadata; strip it so the cache
// holds raw DTO shapes (re-enriched on restore) and snapshots stay DTO-hash-guarded.
function stripMeta(row: SampleTableRowDto): SampleTableRowDto {
  const cleaned = {} as Record<string, unknown>;
  for (const [key, value] of Object.entries(toRaw(row))) {
    if (!key.startsWith('_')) cleaned[key] = value;
  }
  return cleaned as unknown as SampleTableRowDto;
}

export const useSampleTable = defineStore('sample-table-cache', () => {
  const lru = keyedLruCache<SampleTableRowCache>(MAX_ROW_CACHES);
  const rowCaches = lru.data;

  // Snapshot persistence contract — see store/warm-boot.ts. The table view always
  // refetches after seeding from these caches, so disk-seeded (possibly stale) rows
  // revalidate on mount without extra machinery.
  const snapshotDto = 'SampleTableRowDto' as const;
  // SampleTableRowCache is a frontend-defined shape the DTO hash can't guard — bump
  // this whenever the snapshot/SampleTableRowCache shape changes.
  const SNAPSHOT_VERSION = 1;

  type SampleTableSnapshot = {
    version: number;
    rowCaches: Record<string, SampleTableRowCache>;
  };

  function snapshotData(): SampleTableSnapshot {
    // toRaw: IndexedDB's structured clone can't handle Vue's reactive proxies
    return { version: SNAPSHOT_VERSION, rowCaches: toRaw(rowCaches.value) };
  }

  function seedSnapshot(data: unknown) {
    const snapshot = data as Partial<SampleTableSnapshot>;
    if (snapshot.version !== SNAPSHOT_VERSION) return;
    if (Object.keys(rowCaches.value).length > 0) return;
    rowCaches.value = snapshot.rowCaches ?? {};
  }

  function setRowCache(
    filterKey: string,
    rows: SampleTableRowDto[],
    totalRowCount: number,
    roundOffset: number,
    hasMore: boolean
  ) {
    lru.set(filterKey, { filterKey, rows: rows.map(stripMeta), totalRowCount, roundOffset, hasMore });
  }

  onTabResume((elapsed) => {
    if (elapsed >= CACHE_TTL) lru.clear();
  });

  return {
    rowCaches,
    snapshotDto,
    snapshotData,
    seedSnapshot,
    getRowCache: lru.get,
    setRowCache,
    clear: lru.clear,
  };
});
